/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { ReactNode, useRef } from 'react';
import { Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import { useAppDispatch } from 'store';
import { showMessage } from 'store/message/messageSlice';
import * as XLSX from 'xlsx';
import uniqueId from 'lodash/uniqueId';
import DownloadIcon from '@mui/icons-material/Download';

interface IFileProps {
  label?: string;
  labelWidth?: number | string;
  marginBottom?: string;
  onChange: ({ file, rows }: { file: File; rows: any[][] }) => void;
  onReset: () => void;
  children: ReactNode;
  type: 'template1' | 'template2' | 'template3';
}
function FileUpload({
  label,
  labelWidth = '100px',
  marginBottom = '8px',
  onChange,
  onReset,
  children,
  type,
}: IFileProps): JSX.Element {
  const labelStyle: SxProps<Theme> = {
    color: 'mind.black.A0',
    minWidth: labelWidth,
    maxWidth: labelWidth,
    typography: 's13w800',
  };
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUserExcelReset = () => {
    onReset();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      const reader = new FileReader();
      reader.onload = _event => {
        const result = _event.target?.result;
        if (result) {
          const data = new Uint8Array(result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          const rows = jsonData
            .slice(1)
            .filter((row: any[]) => row.some(cell => cell !== undefined && cell !== null && cell !== ''));
          if (rows.length > 0) {
            onChange({ file, rows });
          } else {
            dispatch(showMessage({ message: '엑셀 데이터를 다시 확인해주세요.', variant: 'warning' }));
            handleUserExcelReset();
          }
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };
  const id = uniqueId();
  const handleTemplateExcelDownload = () => {
    const link = document.createElement('a');
    switch (type) {
      case 'template1':
        link.href = '/excel/template1.xlsx';
        link.download = '참여명단 템플릿 2.0.xlsx';
        break;
      case 'template2':
        link.href = '/excel/template2.xlsx';
        link.download = '리더십 진단 검사 문항 템플릿 2.3.xlsx';
        break;
      case 'template3':
        link.href = '/excel/template3.xlsx';
        link.download = '자료수집 템플릿_98문항 2.0.xlsx';
        break;
      default:
        return;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack
      sx={{ marginBottom, marginTop: '1px' }}
      spacing="8px"
      direction={{ xs: 'column', sm: 'column', md: 'row' }}
      alignItems="center"
    >
      {label && (
        <Typography
          sx={{
            ...labelStyle,
            maxWidth: { xs: '100%', sm: '100%', md: labelWidth },
            width: { xs: '100%', sm: '100%', md: labelWidth },
          }}
          noWrap
        >
          {label}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={0}
        sx={theme => ({
          width: '100%',
          display: 'flex',
          border: `1px solid ${theme.palette.mind.grey.border2}`,
          borderRadius: '4px',
        })}
        justifyContent="center"
        alignItems="center"
      >
        <Stack direction="row" spacing={0}>
          <Button
            variant="contained"
            color="primary"
            component="span"
            sx={{ width: '160px', mr: 1 }}
            startIcon={<DownloadIcon />}
            onClick={handleTemplateExcelDownload}
          >
            템플릿 다운로드
          </Button>
          <input
            accept=".xlsx"
            style={{ display: 'none' }}
            id={id}
            type="file"
            onChange={handleFileChange}
            ref={fileInputRef}
          />
          <label htmlFor={id}>
            <Button variant="contained" color="primary" component="span" sx={{ width: 85 }}>
              파일 선택
            </Button>
          </label>
        </Stack>
        {children}
      </Stack>
    </Stack>
  );
}

export default FileUpload;
